import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface WearCalendarProps {
  /** ISO date strings (YYYY-MM-DD or full ISO) of days the fit was worn. */
  wornDates: string[];
  currentVersion: number;
  /** Map of wornDate -> version number worn (to flag previous-version wears). */
  versionByDate?: Record<string, number>;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

export function WearCalendar({ wornDates, currentVersion, versionByDate = {} }: WearCalendarProps) {
  const now = new Date();
  const [view, setView] = useState({ year: now.getFullYear(), month: now.getMonth() });

  const worn = new Set(wornDates.map(dayKey));

  const navigate = (dir: -1 | 1) =>
    setView((prev) => {
      const m = prev.month + dir;
      if (m < 0) return { year: prev.year - 1, month: 11 };
      if (m > 11) return { year: prev.year + 1, month: 0 };
      return { year: prev.year, month: m };
    });

  const firstDay = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells: ({ day: number; key: string } | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${view.year}-${String(view.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, key });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground">Wear Calendar</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-secondary">
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <span className="text-sm font-medium text-foreground min-w-[110px] text-center">
            {MONTHS[view.month]} {view.year}
          </span>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-secondary">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center text-xs text-muted-foreground font-medium">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} />;
          const isWorn = worn.has(cell.key);
          const ver = versionByDate[cell.key];
          const isPrev = isWorn && ver !== undefined && ver < currentVersion;
          return (
            <div
              key={i}
              className={`aspect-square flex items-center justify-center rounded-lg text-xs relative ${
                isWorn ? 'bg-primary text-primary-foreground font-semibold' : 'bg-secondary text-foreground'
              }`}
            >
              {cell.day}
              {isPrev && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-muted-foreground rounded-full"
                  title={`v${ver}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
